import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ClaimSummary,
  PolicySummary,
  PolicyDocumentType,
} from "../../api/services";
import { formatDate as formatDateOnly } from "@ui/shared";
import { formatCurrency } from "../../utils/formatCurrency";

interface PolicyDocumentLink {
  type: PolicyDocumentType;
  labelKey: string;
}

interface Props {
  policy: PolicySummary;
  claim: ClaimSummary;
  documents: PolicyDocumentLink[];
  downloadingDocs: Record<string, boolean>;
  onDownloadDocument: (
    policyUuid: string,
    documentType: PolicyDocumentType,
  ) => void;
}

// formatDate here is fed `policy.startsOn`/`policy.endsOn` — DATE columns
// (YYYY-MM-DD). `formatDateOnly` from @ui/shared uses parseLocalDate so
// the calendar day doesn't shift in US timezones via UTC parsing.
const formatDate = (s: string | null | undefined): string =>
  s ? formatDateOnly(s, { year: "numeric", month: "numeric", day: "numeric" }) : "-";

const ClaimedPolicyCard: React.FC<Props> = ({
  policy,
  claim,
  documents,
  downloadingDocs,
  onDownloadDocument,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const propertyName = policy.unit?.property?.name ?? "";
  const unitName = policy.unit?.name ?? "";

  // Only surface the plan strip while the plan is actively being repaid.
  // Completed/cancelled/failed plans keep the plain claim view.
  const plan =
    claim.installmentPlan && claim.installmentPlan.status === "active"
      ? claim.installmentPlan
      : null;
  // Progress toward fully repaying the plan, clamped 0–100 to defend
  // against any rounding that pushes paid slightly over total.
  const paidPercent = plan
    ? Math.min(
        100,
        Math.max(
          0,
          plan.totalAmount > 0
            ? Math.round((plan.paidAmount / plan.totalAmount) * 100)
            : 0,
        ),
      )
    : 0;

  return (
    <div className="relative overflow-hidden rounded-xl border border-red-200 bg-red-50 shadow-sm">
      <div className="p-5">
        {/* Claim header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-base font-semibold text-danger">
              {t("dashboard.claimBanner.claimSubmitted")}
              {claim.type
                ? ` · ${t(`claimType.${claim.type}`, claim.type)}`
                : ""}
            </p>
            <p className="mt-0.5 text-sm text-gray-700">
              {t("dashboard.claimBanner.requested", {
                amount: formatCurrency(claim.requestAmount),
              })}
              {" · "}
              {new Date(claim.createdAt).toLocaleDateString()}
            </p>
          </div>
          <button
            className="shrink-0 cursor-pointer rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
            onClick={() => navigate(`/claim/${claim.uuid}`)}
          >
            {plan
              ? t("dashboard.claimBanner.seeDetails")
              : t("dashboard.claimBanner.takeAction")}
          </button>
        </div>

        {/* Nested policy details */}
        <div className="mt-4 rounded-lg bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-primary">
            {propertyName} – {unitName}
          </p>
          {policy.account?.companyName && (
            <p className="mt-0.5 text-xs text-muted">
              {t("dashboard.managedBy", {
                company: policy.account.companyName,
              })}
            </p>
          )}

          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <div>
              <p className="text-xs text-muted">{t("dashboard.policy")}</p>
              <p className="font-medium">{policy.name}</p>
            </div>
            <div>
              <p className="text-xs text-muted">{t("dashboard.coverage")}</p>
              <p className="font-medium">
                {formatCurrency(policy.coverageAmount)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted">{t("dashboard.effective")}</p>
              <p className="font-medium">
                {formatDate(policy.startsOn)} – {formatDate(policy.endsOn)}
              </p>
            </div>
          </div>

          {plan && (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <div className="mb-2 flex items-center gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                  {t("dashboard.claimBanner.plan.title")}
                </p>
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                  {t("dashboard.claimBanner.plan.statusActive")}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                <div>
                  <p className="text-xs text-muted">
                    {t("dashboard.claimBanner.plan.remaining")}
                  </p>
                  <p className="font-semibold text-danger">
                    {formatCurrency(plan.remainingAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted">
                    {t("dashboard.claimBanner.plan.paid")}
                  </p>
                  <p className="font-medium">
                    {t("dashboard.claimBanner.plan.paidOfTotal", {
                      paid: formatCurrency(plan.paidAmount),
                      total: formatCurrency(plan.totalAmount),
                    })}
                  </p>
                </div>
                {plan.nextDueDate && (
                  <div>
                    <p className="text-xs text-muted">
                      {t("dashboard.claimBanner.plan.nextPayment")}
                    </p>
                    <p className="font-medium">
                      {t("dashboard.claimBanner.plan.nextPaymentValue", {
                        amount: formatCurrency(plan.monthlyAmount),
                        date: formatDate(plan.nextDueDate),
                      })}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-3">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-green-500 transition-all"
                    style={{ width: `${paidPercent}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-muted">
                  {t("dashboard.claimBanner.plan.progress", {
                    percent: paidPercent,
                  })}
                </p>
              </div>
            </div>
          )}

          <div className="mt-4 border-t border-gray-100 pt-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
              {t("dashboard.documents")}
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {documents.map((doc) => {
                const key = `${policy.uuid}:${doc.type}`;
                return (
                  <button
                    key={doc.type}
                    type="button"
                    className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-info underline hover:text-sky-600 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
                    onClick={() => onDownloadDocument(policy.uuid, doc.type)}
                    disabled={downloadingDocs[key]}
                  >
                    <svg
                      className="h-4 w-4 shrink-0 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    {downloadingDocs[key]
                      ? t("dashboard.downloading")
                      : t(doc.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimedPolicyCard;
